import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

type POSNode = {
  name: string;
  size?: number;
  color?: string;
  targetReached?: string;
  target?: string;
  month?: string;
  children?:POSNode[];
};

type TreemapProps = {
  data: POSNode; // must include root + children
};

export const D3Treemap: React.FC<TreemapProps> = ({ data }) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(400);

  // Observe parent width only
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setWidth(entry.contentRect.width);
        }
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!data || !ref.current) return;
    const height = width / 2; // 👈 height derived from width (aspect ratio 2:1)

    // clear previous render
    d3.select(ref.current).selectAll("*").remove();

    // create hierarchy
    const root = d3.hierarchy<POSNode>(data).sum((d) => d.size || 0);

    // compute treemap layout
    const treemapRoot: d3.HierarchyRectangularNode<POSNode> = d3
      .treemap<POSNode>()
      .size([width, height])
      .padding(2)(root);

    const svg = d3
      .select(ref.current)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")   // responsive width
      .style("height", "auto"); // 👈 height auto

    // draw rects
    const nodes = svg
      .selectAll("g")
      .data(treemapRoot.leaves())
      .enter()
      .append("g")
      .attr("transform", (d) => `translate(${d.x0},${d.y0})`);

    nodes
      .append("rect")
      .attr("width", (d) => d.x1 - d.x0)
      .attr("height", (d) => d.y1 - d.y0)
      .attr("fill", (d) => d.data.color || "#ccc")
      .attr("stroke", "#fff");

    // POS name
    nodes
      .append("text")
      .attr("x", (d) => (d.x1 - d.x0) / 2)
      .attr("y", (d) => (d.y1 - d.y0) / 2 - 5)
      .attr("text-anchor", "middle")
      .attr("fill", "#111")
      .style("font-size", "14px")
      .style("font-weight", "bold")
      .text((d) => d.data.name);

    // percentage (size)
    nodes
      .append("text")
      .attr("x", (d) => (d.x1 - d.x0) / 2)
      .attr("y", (d) => (d.y1 - d.y0) / 2 + 15)
      .attr("text-anchor", "middle")
      .attr("fill", "#333")
      .style("font-size", "12px")
      .text((d) => (d.data.size ? `${d.data.size}%` : ""));

    // tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("padding", "6px 10px")
      .style("background", "#fff")
      .style("border", "1px solid #ccc")
      .style("border-radius", "6px")
      .style("box-shadow", "0 2px 6px rgba(0,0,0,0.15)")
      .style("pointer-events", "none")
      .style("visibility", "hidden");

    nodes
        .on("mouseover", (event, d) => {
          tooltip
            .style("visibility", "visible")
            .style("background", d.data.color || "#fff")   // 👈 use POS color
            .style("color", d3.color(d.data.color || "#000")?.darker(3).formatHex() || "#111") // readable text
            .style("border-color", d.data.color || "#ccc")
            .html(`
              <strong>${d.data.name}</strong><br/>
              Target: ${d.data.target || "0"}<br/>
              Achieved: ${d.data.targetReached || "0"}
              ${d.data.month && d.data.month !== "0" ? `<br/>Target Per Day: ${d.data.month}` : ""}
            `);
        })

      .on("mousemove", (event) => {
        tooltip.style("top", event.pageY + 10 + "px").style("left", event.pageX + 10 + "px");
      })
      .on("mouseout", () => tooltip.style("visibility", "hidden"));

    return () => {
      tooltip.remove();
    };
  }, [data, width]);

  return (
    <>
    <div className="col-12">
      <div className="flex justify-content-start mb-2">
        <div className="flex align-items-center gap-2 p-2 border-round" 
        style={{  backgroundColor: 'var(--blue-50)',  border: '1px solid var(--blue-100)', display: 'inline-flex', maxWidth: 'fit-content' }}>
        <i className="pi pi-info-circle" style={{ color: 'var(--blue-600)', fontSize: '1.25rem' }} />
        <span style={{ color: 'var(--blue-700)' }}>Here values shown are exclusive of tax</span>
        </div>
      </div>
          <div ref={containerRef} style={{ width: "100%",display: "flex", aspectRatio: " 7 / 3",padding: "10px 0",}}>
      <svg ref={ref} width="100%" height={400}></svg>
    </div>
    </div>
    </>

  );
};